// Merchant payout ledger — HTG owed and paid, via MonCash. A payout becomes
// eligible the moment a recipient confirms delivery; admin batches the
// disbursement. Replaces the placeholder.

import { useQuery } from '@tanstack/react-query';
import { Loader2, Banknote, Wallet, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PortalShell } from '../../components/shared/PortalShell';
import { Card, SectionHeader, EmptyState } from '../../components/shared/primitives';
import { MERCHANT_NAV } from '../_navs';
import { payouts, type Payout } from '../../lib/api';
import { formatHtgMinor } from '../../lib/money';

const METHOD_LABEL: Record<string, string> = {
  moncash: 'MonCash', natcash: 'NatCash', manual_bank: 'Bank transfer', cash: 'Cash pickup', other: 'Other',
};
const STATUS_TONE: Record<string, string> = {
  pending: 'bg-amber-50 text-amber-700', paid: 'bg-emerald-50 text-emerald-700',
  failed: 'bg-red-50 text-red-700', held: 'bg-slate-100 text-slate-600',
  approved: 'bg-blue-50 text-blue-700', cancelled: 'bg-slate-100 text-slate-500',
};

export function MerchantPayoutsPage() {
  const q = useQuery({ queryKey: ['merchant', 'payouts'], queryFn: payouts.mine });
  const d = q.data;

  return (
    <PortalShell nav={MERCHANT_NAV} activeId="payouts" title="Payouts" subtitle="What you're owed and what's been paid — in HTG, via MonCash.">
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Stat icon={Wallet} label="Eligible balance" value={d ? formatHtgMinor(d.eligible_htg_minor) : '—'} tone="amber" sub="Confirmed, awaiting disbursement" />
          <Stat icon={CheckCircle2} label="Paid to date" value={d ? formatHtgMinor(d.paid_htg_minor) : '—'} tone="emerald" />
          <Stat icon={Banknote} label="Payout method" value={d?.method ? (METHOD_LABEL[d.method] ?? d.method) : 'Not set'} sub={!d?.method ? 'Set it in Business profile' : undefined} />
        </div>

        <Card padding="p-0">
          {q.isLoading ? (
            <div className="text-center py-12 text-slate-400 text-[13px]"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading…</div>
          ) : !d || d.payouts.length === 0 ? (
            <EmptyState
              icon={Banknote}
              title="No payouts yet"
              body="A payout becomes eligible the moment a recipient confirms a delivery. It's then paid out to your MonCash account."
              action={<Link to="/merchant/orders" className="inline-flex items-center gap-1 text-[13px] font-semibold text-brand-600 hover:underline">See active orders →</Link>}
            />
          ) : (
            <>
              <div className="p-5 border-b border-slate-100">
                <SectionHeader title={`${d.payouts.length} payouts`} subtitle="Most recent first." />
              </div>
              <table className="text-[12.5px] w-full block overflow-x-auto whitespace-nowrap sm:table sm:whitespace-normal">
                <thead className="text-[10.5px] uppercase tracking-wider text-slate-400 bg-slate-50">
                  <tr>
                    <th className="text-left py-2.5 px-5 font-medium">Order</th>
                    <th className="text-left py-2.5 px-3 font-medium">Status</th>
                    <th className="text-left py-2.5 px-3 font-medium">Paid</th>
                    <th className="text-left py-2.5 px-3 font-medium">MonCash ref</th>
                    <th className="text-right py-2.5 px-5 font-medium">HTG</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {d.payouts.map((p) => <PayoutRow key={p.id} p={p} />)}
                </tbody>
              </table>
            </>
          )}
        </Card>
      </div>
    </PortalShell>
  );
}

function Stat({ icon: Icon, label, value, sub, tone }: { icon: React.ComponentType<{ className?: string }>; label: string; value: string; sub?: string; tone?: 'amber' | 'emerald' }) {
  return (
    <Card padding="p-3.5">
      <p className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-400 inline-flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" /> {label}</p>
      <p className={`mt-1 text-[19px] font-bold ${tone === 'amber' ? 'text-amber-600' : tone === 'emerald' ? 'text-emerald-600' : 'text-slate-900'}`}>{value}</p>
      {sub && <p className="text-[11px] text-slate-400">{sub}</p>}
    </Card>
  );
}

function PayoutRow({ p }: { p: Payout }) {
  return (
    <tr className="hover:bg-slate-50">
      <td className="py-3 px-5">
        <span className="font-mono text-cyan-700">{p.request_id ? `REQ-${String(p.request_id).padStart(4, '0')}` : '—'}</span>
        {p.request?.title && <p className="text-[11.5px] text-slate-500">{p.request.title}</p>}
      </td>
      <td className="py-3 px-3"><span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${STATUS_TONE[p.status] ?? 'bg-slate-100 text-slate-600'}`}>{p.status}</span></td>
      <td className="py-3 px-3 text-slate-600">{p.paid_at ? new Date(p.paid_at).toLocaleDateString() : '—'}</td>
      <td className="py-3 px-3 font-mono text-[11px] text-slate-500">{p.moncash_transaction_id ?? p.moncash_reference ?? '—'}</td>
      <td className="py-3 px-5 text-right font-mono text-slate-900">{formatHtgMinor(p.amount_htg_minor, { withSymbol: false })}</td>
    </tr>
  );
}
